import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UserActivityClickTrackingDirective } from '@core';
import { ImageComponent } from '@app/components/image/image.component';
import { resolveImageUrl$ } from '@app/components/image/image-url';
import { TextTranslatePipe } from '@app/pipes/text-translate.pipe';
import { BuilderNode, BuilderState, isBuilderWeatherSource } from '@app/schemas/builder';
import { ConfigService } from '@app/services/config';
import { DEFAULT_BUILDER_STATE, resolveBuilderNode } from './builder-runtime';
import { BUILDER_IMAGE_DEFAULTS, builderNodeStyleText } from './presentation';
import { BuilderRuntimeService } from './builder-runtime.service';
import { BuilderWeatherService } from './weather.service';
import { builderWeatherRequestKey } from './weather-cache';
import { map, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-builder-node',
  templateUrl: './builder-node.component.html',
  styleUrl: './builder-node.component.scss',
  imports: [ImageComponent, TextTranslatePipe, UserActivityClickTrackingDirective],
  host: { style: 'display: contents;' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuilderNodeComponent {
  private readonly runtime = inject(BuilderRuntimeService);
  private readonly config = inject(ConfigService);
  private readonly weather = inject(BuilderWeatherService);

  public readonly node = input.required<BuilderNode>();
  public readonly pageId = input.required<string>();
  public readonly language = input.required<string>();
  public readonly inheritedState = input<BuilderState>(DEFAULT_BUILDER_STATE);

  protected readonly action = computed(() => {
    const node = this.node();
    return node.type === 'button' ? node.action : undefined;
  });
  protected readonly state = computed(() => this.node().type === 'button'
    ? this.runtime.buttonState(this.action())
    : { ...this.inheritedState(), visible: true });
  protected readonly resolved = computed(() => resolveBuilderNode(this.node(), this.state()));
  private readonly weatherRequest = computed(() => {
    const node = this.node();
    if (node.type !== 'data' || !isBuilderWeatherSource(node.source)) return undefined;
    return { key: builderWeatherRequestKey(node.source), request: node.source };
  }, { equal: (previous, next) => previous?.key === next?.key });
  private readonly weatherResult = toSignal(toObservable(this.weatherRequest).pipe(
    switchMap(request => request
      ? this.weather.observe(request.request).pipe(map(snapshot => ({ key: request.key, snapshot })))
      : of(undefined)),
  ));
  protected readonly dataText = computed(() => {
    const node = this.resolved();
    if (node.type !== 'data') return '';
    const result = isBuilderWeatherSource(node.source) ? this.weatherResult() : undefined;
    // A changed location falls back immediately, before the observable effect switches requests.
    const snapshot = result?.key === this.weatherRequest()?.key ? result?.snapshot : undefined;
    return this.runtime.dataText(node.source, node.fallback, this.language(), snapshot);
  });
  protected readonly backgroundUri = computed(() => {
    const background = this.resolved().style?.backgroundImage;
    return background && (background.translations[this.language()] || background.translations[background.fallback])?.uri;
  });
  protected readonly backgroundUrl = toSignal(resolveImageUrl$(
    toObservable(computed(() => this.backgroundUri() || undefined)),
    this.config.files$,
  ));
  protected readonly styles = computed(() => builderNodeStyleText(this.node(), this.state(), this.backgroundUrl()));
  protected readonly imageDefaults = BUILDER_IMAGE_DEFAULTS;
  protected readonly image = computed(() => {
    const node = this.resolved();
    return node.type === 'image'
      ? node.image.translations[this.language()] || node.image.translations[node.image.fallback]
      : undefined;
  });
  protected readonly trackingName = computed(() => this.runtime.trackingName(this.action()));

  protected onClick(event: MouseEvent): void {
    event.preventDefault();
    // Returning to idle must not be followed by the document's wake-on-click listener.
    if (this.action()?.type === 'back') event.stopPropagation();
    this.runtime.execute(this.action(), this.pageId());
  }
}
